import React, { useEffect, useMemo, useState } from "react"
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import clsx from "clsx"
import MyModal from "@common-utils/MyModal"
import ConsultantDetails from "../../modules/business/consultants/details/ConsultantDetails"

interface Props {
     initialData: any[]
}

/**
 * Composant interne pour le détail des coûts d'intercontrat (Tooltip & Locked)
 */
const IntercontratDetailCard = ({
     data,
     dataType,
     onType,
     onClose,
     onConsultantClick,
}: {
     data: any
     dataType: string
     onType: "tooltip" | "locked"
     onClose?: () => void
     onConsultantClick: (id: number, name: string) => void // Callback pour ouvrir le modal
}) => {
     const formatCurrency = (val: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val)
     const formatLongDate = (name: string) => {
          if (!name || !name.includes("/")) return name
          const [month, year] = name.split("/")
          const date = new Date(parseInt(year), parseInt(month) - 1)
          const formatted = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(date)
          return formatted.charAt(0).toUpperCase() + formatted.slice(1)
     }

     return (
          <div className={clsx("bg-white p-5 border rounded-3", onType === "locked" && "border-primary")} style={{ minWidth: "250px", zIndex: 10000, pointerEvents: "auto" }}>
               <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                    <div className="fw-bolder fs-6 text-gray-800 text-capitalize">{formatLongDate(data.name)}</div>
                    {onType === "locked" && (
                         <button className="btn btn-icon btn-sm btn-active-light-primary border-0" onClick={onClose}>
                              <i className="fas fa-times text-muted"></i>
                         </button>
                    )}
               </div>

               <div className="mb-4">
                    <div className="d-flex justify-content-between fs-7 mb-1">
                         <span className="text-muted fw-bold">Coût {dataType === "cumulé" ? "Cumulé" : "Total"} :</span>
                         <span className="text-primary fw-bolder">{formatCurrency(data.cout_total)}</span>
                    </div>
                    <div className="d-flex justify-content-between fs-7">
                         <span className="text-muted fw-bold">ETP {dataType === "cumulé" ? "Moyen" : "Total"} :</span>
                         <span className="text-dark fw-bolder">{data.etp_total.toFixed(2)}</span>
                    </div>
               </div>

               {dataType === "mensuel" && data.details?.length > 0 && (
                    <>
                         <div className="separator separator-dashed mb-3"></div>
                         <div className="max-h-200px overflow-auto pe-2">
                              {data.details.map((c: any, i: number) => (
                                   <div key={i} className="d-flex justify-content-between align-items-center mb-2">
                                        {/* Nom cliquable pour ouvrir les détails */}
                                        <div
                                             onClick={() => onConsultantClick(c.id, c.consultant)}
                                             className="fs-9 fw-bold text-gray-600 text-hover-primary cursor-pointer transition-all"
                                        >
                                             {c.consultant}
                                        </div>
                                        <span className="badge badge-light-primary fw-bolder fs-9">{formatCurrency(c.cout)}</span>
                                   </div>
                              ))}
                         </div>
                    </>
               )}
          </div>
     )
}

const ChartIntercontrat = ({ initialData }: Props) => {
     const [viewType, setViewType] = useState<"chart" | "list">(window.innerWidth < 992 ? "list" : "chart")
     const [dataType, setDataType] = useState<"mensuel" | "cumulé">("mensuel")

     // États pour le verrouillage du Tooltip
     const [selectedPoint, setSelectedPoint] = useState<any | null>(null)
     const [lockedCoords, setLockedCoords] = useState<{ x: number; y: number } | null>(null)

     // État pour gérer le consultant affiché en plein écran
     const [modalConsultant, setModalConsultant] = useState<{ id: number; name: string } | null>(null)

     const formatCurrency = (val: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val)
     const formatShortDate = (name: string) => {
          if (!name || !name.includes("/")) return name
          const [month, year] = name.split("/")
          return `${month}/${year.slice(-2)}`
     }

     // Gestion de la fermeture par Échap (Graphique + Modale)
     useEffect(() => {
          const handleKeyDown = (e: KeyboardEvent) => {
               if (e.key === "Escape") {
                    setSelectedPoint(null)
                    setLockedCoords(null)
                    setModalConsultant(null)
               }
          }
          window.addEventListener("keydown", handleKeyDown)
          return () => window.removeEventListener("keydown", handleKeyDown)
     }, [])

     const data = useMemo(() => {
          if (!initialData) return []
          if (dataType === "mensuel") return initialData

          let cumulativeCout = 0
          let runningSumEtp = 0

          return initialData.map((item, index) => {
               cumulativeCout += item.cout_total
               runningSumEtp += item.etp_total
               return {
                    ...item,
                    cout_total: cumulativeCout,
                    etp_total: runningSumEtp / (index + 1),
               }
          })
     }, [initialData, dataType])

     /**
      * Calcul des totaux et de l'ETP MOYEN
      */
     const totals = useMemo(() => {
          if (!initialData || initialData.length === 0) return { cout: 0, etpMoyen: 0 }

          const sums = initialData.reduce(
               (acc, curr) => ({
                    cout: acc.cout + curr.cout_total,
                    etp: acc.etp + curr.etp_total,
               }),
               { cout: 0, etp: 0 }
          )

          return {
               cout: sums.cout,
               // Calcul de la moyenne de l'ETP sur la période
               etpMoyen: sums.etp / initialData.length,
          }
     }, [initialData])

     const handleChartClick = (state: any) => {
          if (state && state.activeTooltipIndex !== undefined && state.activeCoordinate) {
               setSelectedPoint(data[state.activeTooltipIndex])
               setLockedCoords({
                    x: state.activeCoordinate.x,
                    y: state.activeCoordinate.y,
               })
          }
     }

     useEffect(() => {
          const handleResize = () => {
               if (window.innerWidth < 992) setViewType("list")
          }
          window.addEventListener("resize", handleResize)
          return () => window.removeEventListener("resize", handleResize)
     }, [])

     return (
          <div className="d-flex flex-column h-100 position-relative">
               {/* HEADER: TITRE ET TOTAL KPI */}
               <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-8 gap-4">
                    <div>
                         <h3 className="fw-bolder text-gray-800 m-0 fs-2">Coût de l'intercontrat</h3>
                         <span className="text-muted fw-bold fs-7">Analyse financière des périodes d'inactivité</span>
                    </div>

                    <div className="d-flex w-100 w-md-auto gap-3">
                         <div className="flex-root d-flex flex-column p-3 rounded bg-light-danger border border-dashed border-danger min-w-125px w-md-175px">
                              <span className="fs-9 text-danger fw-boldest text-uppercase">Total Coût</span>
                              <span className="fs-4 fw-boldest text-gray-800">{formatCurrency(totals.cout)}</span>
                         </div>
                         {/* KPI ETP MOYEN mis à jour */}
                         <div className="flex-root d-flex flex-column p-3 rounded bg-light-primary border border-dashed border-primary min-w-125px w-md-125px">
                              <span className="fs-9 text-primary fw-boldest text-uppercase">ETP MOYEN</span>
                              <span className="fs-4 fw-boldest text-gray-800">{totals.etpMoyen.toFixed(2)}</span>
                         </div>
                    </div>
               </div>

               {/* TOOLBAR */}
               <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mb-8 gap-4">
                    <div className="nav-group nav-group-outline bg-light rounded p-0 d-none d-lg-flex">
                         <button
                              className={clsx("btn btn-icon btn-sm border-0", viewType === "chart" ? "bg-white shadow-sm text-primary" : "text-gray-500")}
                              onClick={() => setViewType("chart")}
                         >
                              <i className="fas fa-chart-bar fs-6"></i>
                         </button>
                         <button
                              className={clsx("btn btn-icon btn-sm border-0", viewType === "list" ? "bg-white shadow-sm text-primary" : "text-gray-500")}
                              onClick={() => setViewType("list")}
                         >
                              <i className="fas fa-list fs-6"></i>
                         </button>
                    </div>

                    <div className="nav-group nav-group-outline bg-light rounded p-0 w-100 w-sm-auto d-flex">
                         {["mensuel", "cumulé"].map(type => (
                              <button
                                   key={type}
                                   className={clsx(
                                        "btn btn-sm px-4 py-2 fw-bold border-0 flex-grow-1 text-capitalize",
                                        dataType === type ? "bg-white shadow-sm text-primary" : "text-gray-500"
                                   )}
                                   onClick={() => setDataType(type as any)}
                              >
                                   {type}
                              </button>
                         ))}
                    </div>
               </div>

               {/* CONTENU PRINCIPAL */}
               <div className="flex-grow-1 w-100 position-relative">
                    {viewType === "chart" ? (
                         <>
                              <div style={{ height: "400px" }}>
                                   <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={data} margin={{ top: 10, right: 10, bottom: 20, left: 0 }} onClick={handleChartClick} style={{ cursor: "pointer" }}>
                                             <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                             <XAxis
                                                  dataKey="name"
                                                  tickFormatter={formatShortDate}
                                                  fontSize={11}
                                                  tickLine={false}
                                                  axisLine={false}
                                                  tick={{ fill: "#7E8299" }}
                                                  dy={10}
                                             />
                                             <YAxis yAxisId="left" fontSize={11} axisLine={false} tickLine={false} tickFormatter={val => `${(val / 1000).toFixed(0)}k€`} />
                                             <YAxis yAxisId="right" orientation="right" fontSize={11} axisLine={false} tickLine={false} domain={[0, "auto"]} />

                                             {/* Tooltip standard pour le survol */}
                                             <Tooltip
                                                  content={({ active, payload }) =>
                                                       active && payload && !selectedPoint ? (
                                                            <IntercontratDetailCard
                                                                 data={payload[0].payload}
                                                                 dataType={dataType}
                                                                 onType="tooltip"
                                                                 onConsultantClick={(id, name) => setModalConsultant({ id, name })}
                                                            />
                                                       ) : null
                                                  }
                                                  cursor={{ fill: "#F5F8FA" }}
                                                  wrapperStyle={{ zIndex: 10000, pointerEvents: "none" }}
                                             />

                                             <Legend verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />

                                             <Bar yAxisId="left" name="Coût Intercontrat" dataKey="cout_total" fill="#f4516c" radius={[4, 4, 0, 0]} barSize={35} />
                                             <Line
                                                  yAxisId="right"
                                                  name="ETP (Effectif)"
                                                  type="monotone"
                                                  dataKey="etp_total"
                                                  stroke="#36a3f7"
                                                  strokeWidth={3}
                                                  dot={{ r: 4, fill: "#36a3f7" }}
                                             />
                                        </ComposedChart>
                                   </ResponsiveContainer>
                              </div>

                              {/* TOOLTIP VERROUILLÉ : CENTRÉ VERTICALEMENT */}
                              {selectedPoint && lockedCoords && (
                                   <div
                                        className="position-absolute shadow-lg"
                                        style={{
                                             left: `${lockedCoords.x}px`,
                                             top: "50%",
                                             transform: "translate(-50%, -50%)",
                                             zIndex: 11000,
                                             transition: "left 0.2s ease-out",
                                        }}
                                   >
                                        <IntercontratDetailCard
                                             data={selectedPoint}
                                             dataType={dataType}
                                             onType="locked"
                                             onConsultantClick={(id, name) => setModalConsultant({ id, name })}
                                             onClose={() => {
                                                  setSelectedPoint(null)
                                                  setLockedCoords(null)
                                             }}
                                        />
                                   </div>
                              )}
                         </>
                    ) : (
                         <div className="row g-5 overflow-auto pe-2" style={{ maxHeight: "550px" }}>
                              {data.map((item, idx) => (
                                   <div key={idx} className="col-12 col-md-12 col-lg-6 col-xl-4">
                                        <div className="card shadow-sm border border-gray-200 p-5 h-100">
                                             <IntercontratDetailCard
                                                  data={item}
                                                  dataType={dataType}
                                                  onType="tooltip"
                                                  onConsultantClick={(id, name) => setModalConsultant({ id, name })}
                                             />
                                        </div>
                                   </div>
                              ))}
                         </div>
                    )}
               </div>

               {/* MODAL FULLSCREEN POUR LES DÉTAILS */}
               <MyModal
                    title={modalConsultant?.name} // Nom dans l'en-tête
                    show={modalConsultant !== null}
                    onCloseCallback={() => setModalConsultant(null)}
                    fullscreen // Affichage plein écran
               >
                    {modalConsultant !== null && <ConsultantDetails idConsultantProp={modalConsultant.id} />}
               </MyModal>
          </div>
     )
}

export default ChartIntercontrat
